import type {
  BattleImpact,
  BattlePhase
} from "../battle/domain/battleMachine";

export interface AudioSettings {
  musicEnabled: boolean;
  effectsEnabled: boolean;
}

type SoundName =
  | "swing"
  | "impact"
  | "critical"
  | "correct"
  | "incorrect"
  | "click"
  | "victory"
  | "defeat";

export type AttackSoundStyle = "melee" | "ranged" | "magic";

const SOUND_SOURCES: Record<SoundName, string> = {
  swing: "/assets/audio/kenney/rpg/knifeSlice.ogg",
  impact: "/assets/audio/kenney/impact/impactMetal_medium_001.ogg",
  critical: "/assets/audio/kenney/impact/impactMetal_heavy_000.ogg",
  correct: "/assets/audio/kenney/music/jingles_PIZZI03.ogg",
  incorrect: "/assets/audio/kenney/music/jingles_HIT04.ogg",
  click: "/assets/audio/kenney/ui/click3.ogg",
  victory: "/assets/audio/kenney/voice/you_win.ogg",
  defeat: "/assets/audio/kenney/voice/you_lose.ogg"
};

const SOUND_VOLUMES: Record<SoundName, number> = {
  swing: 0.22,
  impact: 0.42,
  critical: 0.5,
  correct: 0.18,
  incorrect: 0.16,
  click: 0.2,
  victory: 0.36,
  defeat: 0.3
};

const MUSIC_SOURCE = "/assets/audio/music/cave-explorer.mp3";
const MUSIC_VOLUME = 0.0825;

class AudioEngine {
  private settings: AudioSettings = {
    musicEnabled: true,
    effectsEnabled: true
  };

  private unlocked = false;
  private music: HTMLAudioElement | null = null;
  private sounds = new Map<SoundName, HTMLAudioElement>();
  private context: AudioContext | null = null;
  private impactTimers = new Set<number>();

  setSettings(settings: AudioSettings) {
    this.settings = settings;

    if (!this.music) {
      return;
    }

    this.music.muted = !settings.musicEnabled;
    if (settings.musicEnabled && this.unlocked && !document.hidden) {
      void this.playMusic();
    } else {
      this.music.pause();
    }
  }

  unlock() {
    if (!this.unlocked) {
      this.unlocked = true;
      this.prepareMedia();
    }

    if (this.context?.state === "suspended") {
      void this.context.resume();
    }

    if (this.settings.musicEnabled && !document.hidden) {
      void this.playMusic();
    }
  }

  handleVisibilityChange() {
    if (!this.music) {
      return;
    }

    if (document.hidden) {
      this.music.pause();
    } else if (this.settings.musicEnabled && this.unlocked) {
      void this.playMusic();
    }
  }

  playImpact(
    impact: BattleImpact,
    feedback: "correct" | "incorrect",
    attackStyle: AttackSoundStyle = "melee"
  ) {
    if (!this.settings.effectsEnabled || !this.unlocked) {
      return;
    }

    this.playSound(feedback);
    this.playAttackCue(attackStyle);

    const impactTimer = window.setTimeout(() => {
      this.playSound(impact.kind === "critical" ? "critical" : "impact");
      this.playGrunt(impact.target, impact.kind === "critical");
      this.impactTimers.delete(impactTimer);
    }, 190);

    this.impactTimers.add(impactTimer);
  }

  playResult(phase: Exclude<BattlePhase, "playing">) {
    if (!this.settings.effectsEnabled || !this.unlocked) {
      return;
    }

    this.playSound(phase === "won" ? "victory" : "defeat");
    if (phase === "won") {
      this.playVictoryCheer();
    } else {
      window.setTimeout(() => this.playSound("critical"), 120);
    }
  }

  playClick() {
    if (this.settings.effectsEnabled && this.unlocked) {
      this.playSound("click");
    }
  }

  destroy() {
    this.impactTimers.forEach((timer) => window.clearTimeout(timer));
    this.impactTimers.clear();
    this.music?.pause();
    void this.context?.close();
    this.context = null;
    this.music = null;
    this.sounds.clear();
    this.unlocked = false;
  }

  private prepareMedia() {
    this.music = new Audio(MUSIC_SOURCE);
    this.music.loop = true;
    this.music.preload = "auto";
    this.music.volume = MUSIC_VOLUME;
    this.music.muted = !this.settings.musicEnabled;

    Object.entries(SOUND_SOURCES).forEach(([name, source]) => {
      const audio = new Audio(source);
      audio.preload = "auto";
      this.sounds.set(name as SoundName, audio);
    });

    const AudioContextClass = window.AudioContext
      ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AudioContextClass) {
      this.context = new AudioContextClass();
    }
  }

  private async playMusic() {
    if (!this.music || !this.settings.musicEnabled) {
      return;
    }

    this.music.muted = false;
    try {
      await this.music.play();
    } catch {
      // Browsers can still reject playback before a trusted user gesture.
    }
  }

  private playSound(name: SoundName) {
    const template = this.sounds.get(name);
    if (!template) {
      return;
    }

    const sound = template.cloneNode(true) as HTMLAudioElement;
    sound.volume = SOUND_VOLUMES[name];
    sound.play().catch(() => undefined);
  }

  private playAttackCue(style: AttackSoundStyle) {
    if (style === "melee") {
      this.playSound("swing");
      return;
    }

    if (style === "ranged") {
      this.playRangedShot();
      return;
    }

    this.playMagicCast();
  }

  private playRangedShot() {
    const context = this.context;
    if (!context || context.state !== "running") {
      this.playSound("swing");
      return;
    }

    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(980, now);
    oscillator.frequency.exponentialRampToValueAtTime(220, now + 0.12);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.055, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.14);
  }

  private playMagicCast() {
    const context = this.context;
    if (!context || context.state !== "running") {
      this.playSound("critical");
      return;
    }

    const now = context.currentTime;
    [330, 495].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = index === 0 ? "sine" : "triangle";
      oscillator.frequency.setValueAtTime(frequency, now);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 2, now + 0.28);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(index === 0 ? 0.045 : 0.025, now + 0.025);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(now);
      oscillator.stop(now + 0.3);
    });
  }

  private playVictoryCheer() {
    const context = this.context;
    if (!context || context.state !== "running") {
      return;
    }

    const now = context.currentTime + 0.08;
    [523.25, 659.25, 783.99].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = now + index * 0.075;
      oscillator.type = "triangle";
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.045, start + 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.3);
    });
  }

  private playGrunt(target: BattleImpact["target"], critical: boolean) {
    const context = this.context;
    if (!context || context.state !== "running") {
      return;
    }

    const now = context.currentTime;
    const duration = critical ? 0.28 : 0.22;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const filter = context.createBiquadFilter();
    const startFrequency = target === "player" ? 178 : 116;
    const endFrequency = target === "player" ? 108 : 68;

    oscillator.type = target === "player" ? "triangle" : "sawtooth";
    oscillator.frequency.setValueAtTime(startFrequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(endFrequency, now + duration);

    filter.type = "lowpass";
    filter.frequency.setValueAtTime(target === "player" ? 820 : 560, now);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(critical ? 0.1 : 0.065, now + 0.025);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    oscillator.connect(filter);
    filter.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }
}

export const audioEngine = new AudioEngine();
